import { useMemo } from 'react';
import { useAccountStore } from '../stores/accountStore';
import { useAccountIdentityVersion } from '../stores/accountMarketIdentity';
import { describeMarket } from '../lib/kalshiTicker';
import { usePolymarketRelayFills } from '../hooks/usePolymarketRelayFills';
import { usePolymarketUsFills } from '../hooks/usePolymarketUsFills';
import { useUsAccountPresence } from '../hooks/useUsAccountPresence';
import { US_CONFLICT_TITLE, US_PROVISIONAL_TITLE, usDollarsToCents, usPriceDashTitle } from '../lib/fills/usPrice';
import { polyTradeLabel } from '../lib/polymarket/polyTradeLabel';
import { usMarketLabel } from '../lib/polymarketUs/marketLabel';
import { usReadStatus } from '../lib/polymarketUs/readStatus';
import {
  RECENT_FILL_LIMIT,
  buildRecentFillTape,
  type RecentFillTapeRow,
  type UsTapeRow,
} from '../lib/fills/recentFillTape';
import { VenueChip } from './atoms/VenueChip';
import { MarketTypeLeadText } from './cards/cardKit';

function timeAgo(isoString: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/** Local HH:MM of G1's receipt time, shown only as "received", never as the fill time. */
function receivedAt(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Kalshi labels come from the ticker; Poly labels come from the relay's
 *  persisted market identity with the browser cache and a hex preview behind
 *  it. US labels take the K/P shape from the venue slug's team codes, else the
 *  relay catalogue's title, else the slug (#1007, #1225); the slug is the hover title.
 *  `betTeam` is Kalshi-only — Poly's side chip carries no named team. */
function labelFor(row: RecentFillTapeRow): { marketLabel: string; betTeam: string | null; marketType: string | null; title: string } {
  switch (row.venue) {
    case 'kalshi': {
      // #964: an NFL ladder prop's chip carries the player and rung (the chip
      // never shrinks, so a whole bet ran off the row); the stat leads the text.
      const { marketLabel, betTeam, badgeTeam, marketType } = describeMarket(row.ticker, row.side);
      return { marketLabel, betTeam: badgeTeam ?? betTeam ?? null, marketType: marketType ?? null, title: row.ticker };
    }
    case 'polymarket':
      return { marketLabel: polyTradeLabel(row.marketLabel, row.ticker), betTeam: null, marketType: null, title: row.ticker };
    case 'polymarket_us': {
      // #1015: a null slug means the trader's own orders disagree on the market.
      const slug = row.marketSlug ?? 'market disputed';
      const marketLabel = row.marketSlug ? usMarketLabel({ marketSlug: row.marketSlug, marketTitle: row.marketTitle }).label : (row.marketTitle ?? slug);
      return { marketLabel, betTeam: null, marketType: null, title: slug };
    }
    default: {
      const unreachable: never = row;
      return unreachable;
    }
  }
}


/** #1007: exact US amounts; a missing, unverified or disputed value is a dash with its reason, never a number.
 *  #1015: a NO price is 1 − the venue's YES price, shown with a provisional marker, and a row whose stored
 *  evidence conflicts carries one marker (it belongs in no total). */
function UsAmounts({ row }: { row: UsTapeRow }) {
  return (
    <>
      {row.evidenceState === 'conflicting' && (
        <span className="text-[10px] text-amber-400 light:text-amber-700" title={US_CONFLICT_TITLE}>⚠</span>
      )}
      {row.quantity === null ? (
        <span className="text-muted-foreground" title="quantity disputed">—</span>
      ) : (
        <span className="text-muted-foreground">{row.quantity}</span>
      )}
      {row.price === null ? (
        // A dash with its reason; no "¢" on a price that is not known.
        <span className="text-foreground">
          @ <span title={usPriceDashTitle(row.priceBasis)}>—</span>
        </span>
      ) : (
        <span className="text-foreground">
          @ {usDollarsToCents(row.price)}¢
          {row.priceBasis === 'provisional' && (
            <span className="text-muted-foreground/60" title={US_PROVISIONAL_TITLE}>*</span>
          )}
        </span>
      )}
      {row.disputed.includes('executedAt') ? (
        <span className="text-muted-foreground/60" title="time disputed">—</span>
      ) : row.executedAt === null ? (
        <span className="text-muted-foreground/60" title={`time unknown · received ${receivedAt(row.observedAtMs)}`}>—</span>
      ) : (
        <span className="text-muted-foreground/60">{timeAgo(row.executedAt)}</span>
      )}
    </>
  );
}

export function ActivityTicker() {
  const fills = useAccountStore((s) => s.activityTicker);
  const fillsSynced = useAccountStore((s) => s.fillsSynced);
  // #1134: Kalshi labels read market identity imperatively, so a fill already
  // on the tape never learned its matchup when identity arrived later. This
  // counter follows live pair/line changes and actual retirement as well as
  // saved identity. Repeat price frames stay quiet; `labelFor` re-runs below.
  useAccountIdentityVersion((s) => s.version);
  // Read-only history for the authenticated trader. No new poller: this is the
  // existing relay hook on its existing 10s cadence, and Poly rows are display
  // only — they never reach processFill, so Kalshi fill/first-fill/whale audio
  // is untouched and Polymarket emits no sound (#457).
  const poly = usePolymarketRelayFills({ limit: RECENT_FILL_LIMIT });
  // #1007: Polymarket US on the same bounded cadence. Display only, like Poly:
  // US rows never reach processFill, so no sound (the international baseline
  // raises no fill alert either).
  const us = usePolymarketUsFills({ limit: RECENT_FILL_LIMIT });
  // Whether this login has US at all, known from the shared account read even
  // when the fills read has never answered (#987): a US trader whose first
  // fills read fails is told so, and a trader without US keeps the old rail.
  const usPresence = useUsAccountPresence();
  const hasUs = us.hasAccounts ?? usPresence;

  // Both venues merged, deduplicated, newest-first and capped once. Since #769
  // the Kalshi slice already arrives newest-first — `ingestFillBatch` normalizes
  // it at the store, which is the only place that can fix it, because a batch
  // larger than the retention cap used to drop the newest fills before any
  // component could sort them. Sorting here remains load-bearing for the MERGE:
  // two venues interleave, and Poly rows are ordered independently.
  const tapeRows = useMemo(
    () => buildRecentFillTape(fills, poly.fills, RECENT_FILL_LIMIT, us.fills),
    [fills, poly.fills, us.fills],
  );

  // One venue read-status line. A failed presence/read check never means
  // "no US"; retained rows keep their own lifecycle and fill evidence.
  const usFailed = us.status === 'unavailable' || us.status === 'error';
  const usUnchecked = hasUs === null && usFailed;
  const usStatus = hasUs !== false && usFailed
    ? usReadStatus('fills', us.fills.length > 0 ? 'stale' : 'unavailable', {
      reasons: us.reasons,
      detail: usUnchecked ? 'Whether this login has a Polymarket US account could not be checked.'
        : us.fills.length > 0 ? 'Showing the last successful reading; newer fills may be missing.' : undefined,
    })
    : us.hasAccounts === true && us.status === 'ready' && !us.complete
      ? usReadStatus('fills', 'incomplete', { reasons: us.reasons })
      : null;
  const usUnknown = hasUs === null && us.status !== 'idle';
  const usStatusLine = usStatus ? (
    <div className="text-muted-foreground px-3 py-1 text-[10px]" title={usStatus.title}
      data-testid={usUnchecked ? 'recent-fills-us-unchecked' : 'recent-fills-us-status'}>
      {usStatus.text}
    </div>
  ) : null;

  if (tapeRows.length === 0) {
    // #1007: while US is unavailable, incomplete or not yet known, "No recent
    // fills" would be a false claim about the trader's history.
    if (usStatusLine) return usStatusLine;
    if (usUnknown) return null;
    // "No recent fills" is a claim about the trader's activity, so only make it
    // once a source has actually answered. Before that, render nothing rather
    // than assert an empty history. Kalshi's arm is fillsSynced — a VALIDATED
    // fills read (#499) — never the connection status: 'connected' holds even
    // while every fills read fails or arrives malformed. A Kalshi outage still
    // doesn't blank the rail: retained or current Poly rows keep it populated,
    // and vice versa.
    // A complete US account read is also authoritative for a US-only trader.
    // Merely having no US account says nothing about unread K/P history.
    const anySourceAuthoritative = fillsSynced || poly.status === 'ready'
      || (hasUs === true && us.status === 'ready' && us.complete);
    if (!anySourceAuthoritative) return null;
    return (
      <div className="text-muted-foreground px-3 py-2 text-xs">
        No recent fills
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {tapeRows.map((row) => {
        const { marketLabel, betTeam, marketType, title } = labelFor(row);
        return (
          <div
            key={row.key}
            className="hover:bg-muted/50 flex items-center justify-between gap-2 rounded px-2 py-1 text-xs"
          >
            <div className={`flex items-center gap-1.5 overflow-hidden ${marketType ? 'flex-wrap' : ''}`}>
              <VenueChip venue={row.venue} />
              {/* #1015: a US row whose own orders disagree has no side or action; neutral, never a guessed colour. */}
              {row.action === null ? (
                <span className="text-muted-foreground shrink-0 font-medium" title="action disputed">—</span>
              ) : (
                <span
                  className={
                    row.action === 'buy'
                      ? 'shrink-0 font-medium text-green-400'
                      : 'shrink-0 font-medium text-red-400'
                  }
                >
                  {row.action.toUpperCase()}
                </span>
              )}
              {row.side === null ? (
                <span className="bg-muted text-muted-foreground shrink-0 rounded px-1 py-0.5 text-[9px] font-bold" title="side disputed">—</span>
              ) : (
                <span
                  className={`${marketType ? 'max-w-full' : 'max-w-[75%]'} shrink-0 rounded px-1 py-0.5 text-[9px] font-bold [overflow-wrap:anywhere] ${
                    row.side === 'yes'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {betTeam ? `(${betTeam}) ` : ''}
                  {row.side.toUpperCase()}
                </span>
              )}
              <MarketTypeLeadText marketType={marketType} marketLabel={marketLabel} title={title} />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {row.venue === 'polymarket_us' ? (
                <UsAmounts row={row} />
              ) : (
                <>
                  <span className="text-muted-foreground">{row.count}</span>
                  <span className="text-foreground">@ {row.priceCents}¢</span>
                  <span className="text-muted-foreground/60">{timeAgo(row.executedAt)}</span>
                </>
              )}
            </div>
          </div>
        );
      })}
      {usStatusLine}
    </div>
  );
}
